import React, { useState, useEffect } from "react";
import { Modal, Button, List, Input, Empty, Tooltip, Upload, message, Spin } from "antd";
import {
    PaperClipOutlined,
    EditOutlined,
    DeleteOutlined,
    UploadOutlined,
    ExclamationCircleOutlined,
    SaveOutlined,
    EyeOutlined,
    FileOutlined,
    InboxOutlined,
} from "@ant-design/icons";
import courseService from "../../../services/course.service";

const { Dragger } = Upload;

const AttachmentManager = ({ visible, subject, onClose, onSaved }) => {
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(false);

    // Upload
    const [isUploading, setIsUploading] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Renommer
    const [isRenaming, setIsRenaming] = useState(false);
    const [renamingAttachment, setRenamingAttachment] = useState(null);
    const [newFileName, setNewFileName] = useState("");
    const [savingRename, setSavingRename] = useState(false);

    useEffect(() => {
        if (visible && subject?.id) {
            loadAttachments();
        }
    }, [visible, subject?.id]);

    const loadAttachments = async () => {
        if (!subject?.id) return;
        setLoading(true);
        try {
            const data = await courseService.getAttachmentsBySubject(subject.id);
            setAttachments(data || []);
        } catch (error) {
            console.error("Erreur chargement pièces jointes:", error);
        } finally {
            setLoading(false);
        }
    };

    // ========== UPLOAD ==========
    const handleUpload = async (file) => {
        setUploading(true);
        try {
await courseService.uploadAttachmentToSubject(subject.id, file);
            message.success("Pièce jointe uploadée !");
            setIsUploading(false);
            await loadAttachments();
            onSaved?.();
        } catch (error) {
            message.error("Erreur lors de l'upload.");
            console.error("Erreur upload:", error);
        } finally {
            setUploading(false);
        }
        return false;
    };

    // ========== RENOMMER ==========
    const openRename = (attachment) => {
        setRenamingAttachment(attachment);
        setNewFileName(attachment.fileName || "");
        setIsRenaming(true);
    };

    const handleRename = async () => {
        if (!renamingAttachment?.id || !newFileName.trim()) return;
        setSavingRename(true);
        try {
            await courseService.updateAttachment(renamingAttachment.id, newFileName.trim());
            message.success("Pièce jointe renommée !");
            setIsRenaming(false);
            setRenamingAttachment(null);
            await loadAttachments();
            onSaved?.();
        } catch (error) {
            message.error("Erreur lors du renommage.");
        } finally {
            setSavingRename(false);
        }
    };

    // ========== SUPPRIMER ==========
    const handleDelete = (attachmentId) => {
        Modal.confirm({
            title: "Confirmer la suppression",
            icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />,
            content: "Êtes-vous sûr de vouloir supprimer cette pièce jointe ?",
            okText: "Supprimer",
            okType: "danger",
            cancelText: "Annuler",
            onOk: async () => {
                try {
                    await courseService.deleteAttachment(attachmentId);
                    message.success("Pièce jointe supprimée !");
                    await loadAttachments();
                    onSaved?.();
                } catch (error) {
                    message.error("Erreur lors de la suppression.");
                }
            },
        });
    };

    const handleClose = () => {
        setIsUploading(false);
        setIsRenaming(false);
        setRenamingAttachment(null);
        onClose?.();
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return <FileOutlined style={{ fontSize: 20, color: "#1890ff" }} />;
        const ext = fileName.split(".").pop().toLowerCase();
        const iconColors = {
            pdf: "#ff4d4f",
            doc: "#1890ff", docx: "#1890ff",
            xls: "#52c41a", xlsx: "#52c41a",
            ppt: "#fa8c16", pptx: "#fa8c16",
            png: "#722ed1", jpg: "#722ed1", jpeg: "#722ed1", gif: "#722ed1",
            zip: "#faad14", rar: "#faad14",
        };
        return <FileOutlined style={{ fontSize: 20, color: iconColors[ext] || "#1890ff" }} />;
    };

    return (
        <>
            {/* Modal principal */}
            <Modal
                open={visible}
                title={
                    <span>
                        <PaperClipOutlined style={{ marginRight: 8, color: "#1890ff" }} />
                        Pièces jointes : {subject?.title || ""}
                    </span>
                }
                footer={null}
                width="70%"
                onCancel={handleClose}
            >
                {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                        <Spin tip="Chargement des pièces jointes..." />
                    </div>
                ) : (
                    <List
                        dataSource={attachments}
                        locale={{ emptyText: <Empty description="Aucune pièce jointe" /> }}
                        renderItem={(item) => (
                            <List.Item
                                actions={[
                                    <Tooltip title="Voir" key="view">
                                        <Button
                                            type="default"
                                            size="small"
                                            icon={<EyeOutlined />}
                                            onClick={() => window.open(item.fileUrl, "_blank")}
                                            style={{ borderRadius: "6px" }}
                                        >
                                            Voir
                                        </Button>
                                    </Tooltip>,
                                    <Tooltip title="Renommer" key="rename">
                                        <Button
                                            type="primary"
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => openRename(item)}
                                            style={{ borderRadius: "6px" }}
                                        >
                                            Renommer
                                        </Button>
                                    </Tooltip>,
                                    <Tooltip title="Supprimer" key="delete">
                                        <Button
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDelete(item.id)}
                                            style={{ borderRadius: "6px" }}
                                        >
                                            Supprimer
                                        </Button>
                                    </Tooltip>,
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={getFileIcon(item.fileName)}
                                    title={
                                        <a
                                            href={item.fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ color: "#1890ff" }}
                                        >
                                            {item.fileName}
                                        </a>
                                    }
                                    description={
                                        <span style={{ fontSize: 12, color: "#999" }}>
                                            {item.fileName?.split(".").pop().toUpperCase() || "Fichier"}
                                        </span>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                )}

                <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={() => setIsUploading(true)}
                        style={{
                            background: "#1890ff",
                            border: "none",
                            borderRadius: "8px",
                        }}
                    >
                        Ajouter une pièce jointe
                    </Button>
                </div>
            </Modal>

            {/* Modal Upload */}
            <Modal
                open={isUploading}
                title="Ajouter une pièce jointe"
                footer={null}
                onCancel={() => setIsUploading(false)}
                width="50%"
                destroyOnClose
            >
                <Dragger
                    name="file"
                    multiple={false}
                    beforeUpload={handleUpload}
                    showUploadList={false}
                    disabled={uploading}
                    style={{
                        padding: "30px",
                        borderRadius: "8px",
                        border: "2px dashed #1890ff",
                        background: "#f0f5ff",
                    }}
                >
                    {uploading ? (
                        <div>
                            <Spin size="large" />
                            <p style={{ marginTop: 16, color: "#1890ff" }}>
                                Upload en cours...
                            </p>
                        </div>
                    ) : (
                        <div>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined style={{ color: "#1890ff", fontSize: 48 }} />
                            </p>
                            <p className="ant-upload-text" style={{ fontSize: 14 }}>
                                Cliquez ou glissez un fichier ici
                            </p>
                            <p className="ant-upload-hint" style={{ color: "#999" }}>
                                PDF, Word, Excel, Images, etc.
                            </p>
                        </div>
                    )}
                </Dragger>
            </Modal>

            {/* Modal Renommer */}
            <Modal
                open={isRenaming}
                title={
                    <span>
                        <EditOutlined style={{ marginRight: 8 }} />
                        Renommer : {renamingAttachment?.fileName || ""}
                    </span>
                }
                footer={null}
                onCancel={() => {
                    setIsRenaming(false);
                    setRenamingAttachment(null);
                }}
                width="40%"
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
                        Nouveau nom du fichier
                    </label>
                    <Input
                        value={newFileName}
                        onChange={(e) => setNewFileName(e.target.value)}
                        placeholder="Nom du fichier"
                        size="large"
                        onPressEnter={handleRename}
                    />
                </div>
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    block
                    loading={savingRename}
                    onClick={handleRename}
                    disabled={!newFileName.trim()}
                    style={{ borderRadius: "8px" }}
                >
                    Enregistrer
                </Button>
            </Modal>
        </>
    );
};

export default AttachmentManager;